import { mapActions, mapState } from 'vuex';
import { POST_LOADER_DELAY } from '@/constants';
import { ICategory } from '@/services/asset';
import { IProduct } from '@/services/product';
import checks, { ProductChecks } from '@/services/checks';
import { GridColumn, GridForm, GridRow } from '@/components/grid';
import AdBanner from '@/components/AdBanner.vue';
import AssetManagerBox from '@/components/asset-manager/AssetManagerBox.vue';
import AssetManagerRegion from '@/components/asset-manager/AssetManagerRegion.vue';
import CheckList from '@/components/CheckList.vue';
import ChromeEditor from '@/components/chrome/Editor.vue';
import ChromeEditorPanel from '@/components/chrome/EditorPanel.vue';
import FieldBase from '@/components/field/Base.vue';
import FullscreenButton from '@/components/FullscreenButton.vue';
import InfoBox from '@/components/InfoBox.vue';
import PillContent from '@/components/pill/PillContent.vue';
import PricingTable from '@/components/PricingTable.vue';
import PropsTable from '@/components/PropsTable.vue';
import ProductSizeTable from '@/components/product/ProductSizeTable.vue';
import ShareBox from '@/components/ShareBox.vue';
import ShowGuide from '@/components/ShowGuide.vue';
import SidebarPanel from '@/components/sidebar/SidebarPanel.vue';
import WashSelector from '@/components/wash-icons/WashSelector.vue';
import { askClone, askDelete, createProduct } from '../../common';
import messages from '../../messages';
import ProductCare from './ProductCare.vue';
import ProductComponents from './ProductComponents.vue';
import ProductCosts from './ProductCosts.vue';
import ProductMaterials from './ProductMaterials.vue';
import checklistActions from './checks';

export default (storeName: string) => ({
  data() {
    const self = this;
    return {
      checklistActions: checklistActions(this),
      fetched: false,
      guide: [
        {
          point: '#assetRegion',
        },
        {
          ref: 'assets',
          onEnter: () => {
            self.$store.state.isMobile
              ? self.toggleSidebar(false)
              : self.selectPanel('assets');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          point: '#propertiesTable',
          dialog: {
            selector: '#assetRegion',
            content: 'Add detailed images of your product including technical drawings, multi-angle and close-up shots.',
          },
        },
        {
          ref: 'properties',
          point: '#pricingTable',
          dialog: {
            selector: '#propertiesTable',
            content: 'Add valuable information such as dimensions, colours, finishing, weight, composition etc.',
          },
        },
        {
          ref: 'pricing',
          point: '#productCare',
          dialog: {
            selector: '#pricingTable',
            content: 'Add price information such as price per item, price for bulk orders, discount prices etc.',
          },
        },
        {
          ref: 'care',
          point: '#materialsTable',
          onEnter: () => {
            // don't show washing panel on mobile
            self.isMobile
              ? self.toggleSidebar(false)
              : self.selectPanel('washing');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          dialog: {
            selector: '#productCare',
            content: 'Complete care and washing instructions to ensure customers know how to care for the product appropriately.',
          },
        },
        {
          ref: 'materials',
          point: '#componentsTable',
          dialog: {
            selector: '#materialsTable',
            content: 'List the materials that make up your product for reference and analysis.',
          },
        },
        {
          ref: 'components',
          point: '[class*="SidebarWrapper_toolbar"] [title="Costs"]',
          dialog: {
            selector: '#componentsTable',
            content: 'List the components that make up your product for reference and analysis.',
          },
        },
        {
          ref: 'costs',
          point: '#sizeTable',
          onEnter: () => {
            self.selectPanel('costs');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          dialog: {
            selector: '#costsPanel',
            content: 'Get production cost estimates in any currency, by adding the unit cost price of your materials, components and other production related items. Wholesale and retail prices will also be recommended based on industry standard calculations.',
          },
        },
        {
          ref: 'sizes',
          point: '[class*="SidebarWrapper_toolbar"] [title="Checks"]',
          dialog: {
            selector: '#sizeTable',
            content: 'Add size categories and measurments to help enforce consistency between production and customer expectations.',
          },
        },
        {
          onEnter: () => {
            self.selectPanel('checks');
            return new Promise(resolve => setTimeout(resolve, 250));
          },
          dialog: {
            selector: '[class*="SidebarWrapper_panelsOuter"]',
            content: 'Try and complete all the tasks listed to help make more detailed tech packs, provide your customers with more accurate information and increase confidence during production, distribution and sales.',
          },
        },
      ],
      settings: {
        name: {
          title: 'Name',
        },
        notes: {
          title: 'Notes',
          type: 'textarea',
          params: {
            newline: true,
          },
        },
      },
    };
  },
  computed: {
    ...mapState(['isMobile']),
    ...mapState('chromeEditor', ['panel']),
    ...mapState(storeName, [
      'data',
      'resources',
      'view',
    ]),
    assetRecord() {
      return {
        type: ICategory.Product,
        type_id: this.data.id,
      };
    },
    localValue: {
      get(): IProduct {
        return this.data;
      },
      set(value: IProduct) {
        this.updateProduct(value);
      },
    },
    listOfChecks() {
      return checks(ProductChecks, this.data);
    },
    productId() {
      return this.$route.params.productId;
    },
    shareUrl() {
      return window.location.href;
    },
  },
  methods: {
    ...mapActions('chromeEditor', [
      'selectPanel',
      'toggleSidebar',
    ]),
    ...mapActions(storeName, [
      'clone',
      'create',
      'createResource',
      'load',
      'updateProduct',
    ]),
    askClone() {
      const { id, name } = this.data;
      askClone
        .bind(this)(id, name)
        .then((cloneId: false | string) => (
          cloneId && this.$router.push({
            name: 'product',
            params: {
              productId: cloneId,
            },
          })
        ));
    },
    askDelete() {
      const { id, name } = this.data;
      askDelete
        .bind(this)(id, name)
        .then((deleted: boolean) => (
          deleted && this.$router.replace({
            name: 'products',
          })
        ));
    },
    async fetchData() {
      // load data, populate
      const result: IProduct = await this.load(this.productId);
      await this.assetComposerSyncAssets(
        result.assets.map(asset => ({
          ...asset,
          type_id: this.data.id,
        })),
      );

      return result;
    },
    createRecord() {
      createProduct(this);
    },
    createResourceDialog(resourceType: string) {
      const self = this;
      this.$store.commit('dialog/CREATE', {
        compact: true,
        component: {
          mounted() {
            // remove focus from dialog buttons
            setTimeout(() => {
              this.$el.querySelector('input').focus();
            }, 100);
          },
          data: () => ({
            message: '',
            resourceName: '',
          }),
          methods: {
            create() {
              if (!this.resourceName.trim()) {
                this.message = `Please enter a ${resourceType} name`;
                this.$el.querySelector('input').focus();
                return;
              }

              this.$store.commit('loader/ADD', {
                title: `Creating ${resourceType}`,
                promise: self
                  .createResource({
                    type: resourceType,
                    name: this.resourceName,
                  })
                  .then(() => {
                    // close dialog (assuming this is the only one open)
                    const { dialogs, REMOVE } = this.$parent;
                    REMOVE(dialogs[0].id);
                  }),
              });
            },
          },
          template: `
            <div style="width: 260px; padding: 0;">
              <div>
                <input
                  v-model="resourceName"
                  @keyup="message = ''"
                  @keyup.enter="create"
                  placeholder="Name"
                  aria-label="Name" />
              </div>
              <transition name="transition fade-down">
                <p class="error" v-if="message">{{ message }}</p>
              </transition>
            </div>
          `,
        } as IView,
        buttons: [
          {
            text: `Create ${resourceType}`,
            click: (dialog: any) => dialog.component[0].create(),
          },
          {
            text: 'Close',
            click: (dialog: any) => dialog.close(),
          },
        ],
      });
    },
    focusOnResourceName(ref: string, row: any) {
      // wait for dialog to close before scrolling into view
      setTimeout(() => {
        const container = this.$refs[ref].$el;
        // litte hack. multiple by the number of fields in row
        // to always pick the first input field in row
        const input = container.querySelectorAll('input')[row.index * 2];
        if (!input.value) {
          input.focus();
          input.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 200);
    },
    print() {
      window.print();
    },
    selectLastRowDropdown(ref: string) {
      setTimeout(() => {
        // select the first dropdown of last row
        // (materials has 2 dropdowns)
        const index = ref === 'materials' ? 2 : 1;

        // a little long winded just incase timer is too fast for device processor
        const dropdownSelector = '[class*="Dropdown_dropdown_"]';
        const dropdowns = this.$refs[ref].$el.querySelectorAll(dropdownSelector);
        const lastDropdown = dropdowns[dropdowns.length - index];
        lastDropdown && lastDropdown.click();
      }, 100);
    },
    selectRef(name: string) {
      this.selectPanel('info');

      // wait for animation to finish before focusing on element
      setTimeout(() => {
        const { $el } = this.$refs[name][0];
        $el.querySelector('input,textarea').focus();
      }, 500);
    },
    save() {
      const promise = this.$store
        .dispatch(`${storeName}/save`)
        .catch((error: any) => {
          console.error(error);

          this.$store.commit('dialog/CREATE', {
            title: 'Save error',
            content: messages.ERROR_PRODUCT_SAVE,
            compact: true,
            delay: POST_LOADER_DELAY,
            buttons: [{
              text: 'Close',
              click: (message: any) => message.close(),
            }],
          });
        });

      this.$store.commit('loader/ADD', {
        title: 'Saving',
        promise,
      });
    },
  },
  components: {
    AdBanner,
    AssetManagerBox,
    AssetManagerRegion,
    CheckList,
    ChromeEditor,
    ChromeEditorPanel,
    FieldBase,
    FullscreenButton,
    GridColumn,
    GridForm,
    GridRow,
    InfoBox,
    PillContent,
    PricingTable,
    ProductCare,
    ProductCosts,
    ProductComponents,
    ProductMaterials,
    PropsTable,
    ProductSizeTable,
    ShareBox,
    ShowGuide,
    SidebarPanel,
    WashSelector,
  },
} as IView);
